import { Helmet } from "@/lib/helmet";
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { withCurrentYear } from '@/lib/currentYear';

interface SEOHelmetProps {
  title: string;
  description: string;
  type?: 'website' | 'article' | 'product';
  url: string;
  image?: string;
  structuredData?: object;
  additionalSchemas?: object[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noIndex?: boolean;
  // Product-specific props
  productBrand?: string;
  productPrice?: number;
  productCurrency?: string; // Will use feed_settings.currency if not provided
  preloadImage?: string; // LCP image to preload for performance
}

export const SEOHelmet = ({
  title: rawTitle,
  description: rawDescription,
  type = 'website',
  url,
  image,
  structuredData,
  additionalSchemas = [],
  author,
  publishedTime,
  modifiedTime,
  noIndex = false,
  productBrand,
  productPrice,
  productCurrency, // Will be set from settings.currency if not provided
  preloadImage,
}: SEOHelmetProps) => {
  const { data: settings, isLoading } = useSiteSettings();

  // Never ship a stale year in titles/descriptions
  const title = withCurrentYear(rawTitle);
  const description = withCurrentYear(rawDescription);

  // Use settings currency if productCurrency not explicitly provided
  const currency = productCurrency || settings?.currency || 'EUR';

  // Return basic meta tags during loading to prevent errors
  if (!settings || isLoading) {
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        {image && <meta property="og:image" content={image} />}
      </Helmet>
    );
  }

  const siteTitle = settings.site_name;
  
  // Smart title truncation: For short titles, add site name. For long titles, skip it.
  const titleWithSite = `${title} | ${siteTitle}`;
  const fullTitle = titleWithSite.length <= 60 ? titleWithSite : title;
  
  const metaDescription = description || withCurrentYear(settings.default_meta_description) || 
    'Vergleichen Sie jetzt die beliebtesten Kreditkarten Deutschlands! Finden Sie das beste Angebot für Ihre Bedürfnisse.';
  // Use provided image, fallback to default OG image
  const ogImage = image || (settings.default_og_image.startsWith('http') 
    ? settings.default_og_image 
    : `${settings.domain}${settings.default_og_image}`);
  const articleAuthor = author || settings.default_author;

  // Generate organization schema if data is available
  const organizationSchema = settings.organization_name ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": settings.organization_name,
    "url": settings.domain,
    "logo": settings.site_logo_url,
    "contactPoint": settings.organization_email ? {
      "@type": "ContactPoint",
      "email": settings.organization_email,
      "contactType": "customer service"
    } : undefined,
    "sameAs": [
      settings.organization_social?.facebook,
      settings.organization_social?.twitter,
      settings.organization_social?.instagram,
      settings.organization_social?.linkedin,
    ].filter(Boolean)
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={url} />
      <html lang={settings.language} />

      {/* Preload LCP image for faster paint */}
      {preloadImage && (
        <link 
          rel="preload" 
          as="image" 
          href={preloadImage}
          fetchPriority="high"
        />
      )}

      {/* Robots Meta Tags for AI Agents */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow" />
        </>
      )}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={settings.language} />

      {/* Article specific OG tags */}
      {type === 'article' && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      
      {/* Product-specific OG tags */}
      {type === 'product' && productBrand && (
        <meta property="product:brand" content={productBrand} />
      )}
      {type === 'product' && productPrice !== undefined && (
        <>
          <meta property="product:price:amount" content={productPrice.toString()} />
          <meta property="product:price:currency" content={currency} />
        </>
      )}
      
      {/* Freshness signal */}
      {modifiedTime && (
        <meta property="og:updated_time" content={modifiedTime} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      {settings.twitter_handle && (
        <>
          <meta name="twitter:site" content={settings.twitter_handle} />
          <meta name="twitter:creator" content={settings.twitter_handle} />
        </>
      )}

      {/* Structured Data - Page specific */}
      {structuredData && (
        Array.isArray(structuredData) ? (
          structuredData.map((schema, idx) => (
            <script key={`main-schema-${idx}`} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )
      )}

      {/* Structured Data - Organization */}
      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {/* Additional Structured Data Schemas */}
      {additionalSchemas.map((schema, index) => (
        schema && (
          <script key={`schema-${index}`} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )
      ))}
    </Helmet>
  );
};
